import { animated, useSpring } from '@react-spring/web';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import * as React from 'react';
import { Controller, FieldError, FieldErrorsImpl, Merge, useFormContext } from 'react-hook-form';
import { IGoogleReviewAutoReplySettings, ISatisfactionSurvey } from '../../../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../models';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { THEME } from '../../../../components-v2/Theme/theme';
import { Checkbox } from '../../../../components/Checkbox';
import { DeprecatedTextArea } from '../../../../components/DeprecatedTextArea';
import { StarRating } from '../../../../components/surveys/StarRating';
import { TextInputFormField } from '../../../../components/TextInputFormField';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { styleSheet } from '../styles';
import { AutoReplyModeOptions } from '../../../../components/googleBusinessProfiles/ReviewAutoReply/AutoReplyModeOptions';
import { ReviewAutoReplyPreview } from '../../../../components/googleBusinessProfiles/ReviewAutoReply/ReviewAutoReplyPreview';
import { styleSheet as autoReplyStyleSheet } from '../../../../components/googleBusinessProfiles/ReviewAutoReply/styles';

enum ReviewRequestInputIds {
	Link = 'review-request-link-input',
	LinkType = 'review-request-link-type-input',
	Message = 'review-request-message-input',
}

const KnownReviewSites = {
	'Better Business Bureau': [/\.(bbb)\./gi],
	G2: [/\.?(g2)\./gi],
	Google: [/\.?(google)\./gi],
	Trustpilot: [/\.?(trustpilot)\./gi],
	Yelp: [/\.?(yelp)\./gi],
};

import { STAR_RATING_LIMIT } from '../utils';

const LIMIT = STAR_RATING_LIMIT;

const ErrorMessage = ({ error }: { error: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>> }) => {
	if (!error) {
		return null;
	}
	return <p className={css(styleSheet.errors)}>{error as React.ReactNode}</p>;
};

export interface IReviewRequestOptionsProps {
	survey: ISatisfactionSurvey;
	autoReplySettings?: IGoogleReviewAutoReplySettings;
	onAutoReplySettingsChange?: (settings: IGoogleReviewAutoReplySettings) => void;
	onSaveAutoReplySettings?: () => void;
	isSavingAutoReplySettings?: boolean;
	hasUnsavedAutoReplySettings?: boolean;
}

export const ReviewRequestOptionsBase: React.FC<IImpersonationContextComponentProps & IReviewRequestOptionsProps> = ({
	impersonationContext,
	survey,
	autoReplySettings,
	onAutoReplySettingsChange,
	onSaveAutoReplySettings,
	isSavingAutoReplySettings,
	hasUnsavedAutoReplySettings,
}) => {
	const userSession = useUserSession();
	const { control, watch, reset, formState } = useFormContext<ISatisfactionSurvey>();
	const formValues = watch();

	const defaultReviewLink = (impersonationContext?.account?.features?.surveys || userSession.account.features?.surveys)
		?.defaultReviewLink;
	const isArchived = !!survey?.archivedDate;
	const isAiEnabled = !!(
		impersonationContext?.account?.features?.contentGeneration || userSession?.account?.features?.contentGeneration
	)?.enabled;

	React.useEffect(() => {
		if (!survey?.id && !formValues?.reviewRequestSettings?.reviewUrl && !formState.isDirty) {
			reset(values => ({
				...values,
				reviewRequestSettings: {
					...values.reviewRequestSettings,
					reviewUrl: defaultReviewLink,
				},
			}));
		}
	}, [defaultReviewLink, reset, survey?.id, formValues?.reviewRequestSettings?.reviewUrl, formState.isDirty]);

	const onInputBlur = React.useCallback(() => {
		let reviewUrl = formValues?.reviewRequestSettings?.reviewUrl;
		let reviewType = formValues?.reviewRequestSettings?.reviewType;
		if (reviewUrl) {
			if (!/^http[s]?:\/\//gim.test(formValues?.reviewRequestSettings?.reviewUrl)) {
				reviewUrl = `https://${formValues?.reviewRequestSettings?.reviewUrl}`;
			}

			if (!reviewType) {
				const match = Object.entries(KnownReviewSites).find(entry =>
					entry[1].some(x => x.test(formValues.reviewRequestSettings.reviewUrl))
				);
				if (match) {
					reviewType = match[0];
				}
			}
		}
		reset(values => ({
			...values,
			reviewRequestSettings: {
				...values.reviewRequestSettings,
				reviewRequest: formValues.reviewRequestSettings.reviewRequest,
				reviewUrl,
				reviewType,
			},
		}));
	}, [formValues.reviewRequestSettings, reset]);

	const onStarRatingOptionCheckChanged = React.useCallback(
		(value: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const indexOfMatch = formValues?.reviewRequestSettings?.ratings?.findIndex((x: number) => x === value);

			if (indexOfMatch > -1 && !e.target.checked) {
				const nextRatings: number[] = (formValues?.reviewRequestSettings?.ratings || []).slice();
				nextRatings.splice(indexOfMatch, 1);
				nextRatings.sort((a, b) => a - b);

				reset(values => ({
					...values,
					reviewRequestSettings: {
						...values.reviewRequestSettings,
						ratings: nextRatings,
					},
				}));
				return;
			}
			if (e.target.checked) {
				const nextRatings: number[] = (formValues?.reviewRequestSettings?.ratings || []).slice();
				nextRatings.push(value);
				nextRatings.sort((a, b) => a - b);
				reset(values => ({
					...values,
					reviewRequestSettings: {
						...values.reviewRequestSettings,
						ratings: nextRatings,
					},
				}));
			}
		},
		[formValues.reviewRequestSettings.ratings, reset]
	);
	const animationStyles = useSpring({
		from: {
			height: 0,
			opacity: 0,
		},
		to: {
			height: formValues?.reviewRequestSettings?.isEnabled ? 604 : 0,
			opacity: formValues?.reviewRequestSettings?.isEnabled ? 1 : 0,
		},
	});

	return (
		<section className={css(styleSheet.section)}>
			<h3 className={css(styleSheet.sectionTitle)}>Google Review</h3>

			<FormFieldWrapper
				name='reviewRequestSettings.isEnabled'
				control={control}
				title='Ask to leave a review when someone gives you the following ratings:'
				description='Enable review requests for specific star ratings.'
			>
				{({ value, onChange }) => (
					<Toggle
						disabled={isArchived}
						id='edit-survey-review-request-toggle'
						isOn={value}
						onToggleCheckChanged={onChange}
						uncheckedColor={grayIconFill}
					/>
				)}
			</FormFieldWrapper>
			<animated.div style={animationStyles}>
				<fieldset
					className={css(
						styleSheet.fieldset,
						styleSheet.fieldGroup,
						formValues?.reviewRequestSettings?.isEnabled
							? styleSheet.reviewRequestSettingsEnabled
							: styleSheet.reviewRequestSettingsDisabled
					)}
					disabled={isArchived || !formValues?.reviewRequestSettings?.isEnabled}
				>
					<div className={css(styleSheet.constrainedFieldGroup)}>
						<Controller
							name='reviewRequestSettings.reviewUrl'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => {
								fieldProps.onBlur = onInputBlur;
								return (
									<TextInputFormField
										disabled={isArchived}
										id={ReviewRequestInputIds.Link}
										label='Link to the review page (Google, Yelp, etc.)'
										labelStyles={[styleSheet.label]}
										type='text'
										{...fieldProps}
									/>
								);
							}}
						/>
						<ErrorMessage error={formState.errors?.reviewRequestSettings?.reviewUrl?.message} />
						<Controller
							name='reviewRequestSettings.reviewType'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => {
								fieldProps.onBlur = onInputBlur;
								return (
									<TextInputFormField
										disabled={isArchived}
										id={ReviewRequestInputIds.LinkType}
										label='What type of review is it above?'
										labelStyles={[styleSheet.label]}
										{...fieldProps}
										styles={[styleSheet.reviewRequestLinkTypeInput]}
										type='text'
									/>
								);
							}}
						/>
						<ErrorMessage error={formState.errors?.reviewRequestSettings?.reviewType?.message} />
					</div>
				</fieldset>
				<fieldset
					className={css(
						styleSheet.fieldset,
						baseStyleSheet.verticalStack,
						formValues?.reviewRequestSettings?.isEnabled
							? styleSheet.reviewRequestSettingsEnabled
							: styleSheet.reviewRequestSettingsDisabled
					)}
					disabled={isArchived || !formValues?.reviewRequestSettings?.isEnabled}
				>
					<div className={css(styleSheet.fieldGroup)}>
						{Array.from({ length: LIMIT }).map((_, i) => {
							const count = LIMIT - i;
							return (
								<Controller
									key={`edit-survey-review-request-${count}stars`}
									name='reviewRequestSettings.ratings'
									control={control}
									render={({ field: { ref, ...fieldProps } }) => {
										fieldProps.onChange = onStarRatingOptionCheckChanged(count);
										const isChecked = formValues?.reviewRequestSettings?.ratings?.indexOf(count) > -1;
										return (
											// @ts-ignore - value is a number[]
											<Checkbox
												checked={isChecked}
												disabled={isArchived}
												id={`edit-survey-review-request-${count}stars`}
												key={`edit-survey-review-request-${count}stars`}
												{...fieldProps}
											>
												<div className={css(styleSheet.starRating)}>
													<span>{`${count}/${LIMIT}`}</span>
													<StarRating fillColor={THEME.colors.pill.yellow.icon} readonly total={LIMIT} value={count} />
												</div>
											</Checkbox>
										);
									}}
								/>
							);
						})}
						<ErrorMessage error={formState.errors?.reviewRequestSettings?.ratings?.message} />
					</div>
					<div className={css(styleSheet.divider)} />
					<div className={css(styleSheet.constrainedFieldGroup)}>
						<label className={css(styleSheet.label)} htmlFor={ReviewRequestInputIds.Message}>
							Request shown after a positive rating is left:
						</label>
						<Controller
							name='reviewRequestSettings.reviewRequest'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => {
								return (
									<DeprecatedTextArea
										disabled={isArchived}
										inputClassName={css(styleSheet.reviewRequestMessage)}
										inputId={ReviewRequestInputIds.Message}
										{...fieldProps}
									/>
								);
							}}
						/>
						<ErrorMessage error={formState.errors?.reviewRequestSettings?.reviewRequest?.message} />
					</div>
				</fieldset>
			</animated.div>
			{isAiEnabled && onAutoReplySettingsChange && formValues?.reviewRequestSettings?.isEnabled ? (
				<>
					<div className={css(styleSheet.divider)} />
					<ReviewAutoReplyPreview
						impersonationContext={impersonationContext}
						settings={autoReplySettings}
						onSaveSettings={onSaveAutoReplySettings}
						isSavingSettings={isSavingAutoReplySettings}
						hasUnsavedSettings={hasUnsavedAutoReplySettings}
					>
						<h3 className={css(styleSheet.sectionTitle)}>AI Auto-Reply to Google Reviews</h3>
						<p className={css(autoReplyStyleSheet.accountWideCallout)}>
							<strong>This is an account-wide setting.</strong> It applies to every new Google review your account
							receives, no matter which survey generated it.
						</p>
						<AutoReplyModeOptions settings={autoReplySettings} onChange={onAutoReplySettingsChange} />
					</ReviewAutoReplyPreview>
				</>
			) : null}
		</section>
	);
};

export const ReviewRequestOptions = inject(ImpersonationContextKey)(ReviewRequestOptionsBase);
